import { trackDesignerInteraction } from '@utils/gtm'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
} from 'react'
import { TCanvas } from './Canvas'
import { TEditorConfig, TDesignSide } from './editor.types'
import { purelyMultiColorProducts } from './editor-products.config'
import { useSearchParams } from 'next/navigation'

interface IEditorData {
  canvasConfig: Record<TDesignSide, TCanvas>
  updateCanvasConfig: Dispatch<SetStateAction<Record<TDesignSide, TCanvas>>>
  editorConfig: TEditorConfig
  activeDesignSide: TDesignSide
  setActiveDesignSide: Dispatch<SetStateAction<TDesignSide>>
}

const EditorDataContext = createContext<IEditorData | undefined>(undefined)

export function EditorDataProvider({
  children,
  value,
}: {
  children: ReactNode
  value: IEditorData
}) {
  return (
    <EditorDataContext.Provider value={value}>
      {children}
    </EditorDataContext.Provider>
  )
}

const updateCanvasSideConfig = (canvasConfig, side, updates) => {
  return {
    ...canvasConfig,
    [side]: {
      ...canvasConfig?.[side],
      ...updates,
    },
  }
}

export function useCurrentCanvasSide() {
  const context = useContext(EditorDataContext)

  if (context === undefined) {
    throw new Error(
      'useCurrentCanvasSide must be used within a EditorDataContext'
    )
  }

  return {
    canvasConfig: context.canvasConfig[context.activeDesignSide],
    updateCanvasConfig: (changes) => {
      context.updateCanvasConfig(
        updateCanvasSideConfig(
          context?.canvasConfig,
          context?.activeDesignSide,
          changes
        )
      )
    },
  }
}

export function useWholeCanvasConfig() {
  const context = useContext(EditorDataContext)

  if (context === undefined) {
    throw new Error(
      'useWholeCanvasConfig must be used within a EditorDataContext'
    )
  }

  return {
    wholeCanvasConfig: context.canvasConfig,
    updateBothCanvasSides: (changes) => {
      context.updateCanvasConfig(changes)
    },
  }
}

export function useDesignSide() {
  const context = useContext(EditorDataContext)
  if (context === undefined) {
    throw new Error('useDesignSide must be used within a EditorDataContext')
  }
  return {
    activeDesignSide: context.activeDesignSide,
    setActiveDesignSide: context.setActiveDesignSide,
  }
}

export function useEditorConfig() {
  const context = useContext(EditorDataContext)
  if (context === undefined) {
    throw new Error('useEditorConfig must be used within a EditorDataContext')
  }
  return context.editorConfig
}

export function useMultiColorMode() {
  const context = useContext(EditorDataContext)
  if (context === undefined) {
    throw new Error('useMultiColorMode must be used within a EditorDataContext')
  }

  return {
    multiColorModeIsOn:
      context.editorConfig?.productColorOption === 'Multi Color (CMYK)' ||
      purelyMultiColorProducts?.includes(context?.editorConfig?.productSlug),
  }
}

export function useWidget(type) {
  const context = useContext(EditorDataContext)
  const query = useSearchParams()
  if (context === undefined) {
    throw new Error(
      'useCurrentCanvasSide must be used within a EditorDataContext'
    )
  }

  const activeCanvas = context?.canvasConfig?.[context?.activeDesignSide]
  const canvasWidgets = activeCanvas?.widgets
  const widgetIndex = activeCanvas?.widgets?.findIndex(
    (widget) => widget.type === type
  )
  const activeWidget = activeCanvas?.widgets[widgetIndex]

  const updateWidget = (changes) => {
    canvasWidgets?.splice(widgetIndex, 1, {
      ...activeWidget,
      config: {
        ...activeWidget?.config,
        ...changes,
      },
    })

    context.updateCanvasConfig(
      updateCanvasSideConfig(context?.canvasConfig, context?.activeDesignSide, {
        widgets: canvasWidgets,
        activeWidgetId: activeWidget?.id,
        activeWidgetType: activeWidget?.type,
      })
    )

    trackDesignerInteraction({
      event: 'designer-interaction',
      designerName: 'editor',
      componentName: `${type} widget`,
      componentInformation: changes?.assetSvgData
        ? 'Updated artwork used in the widget.'
        : JSON.stringify(changes),
      selectedProductSlug: query.get('slug'),
      displayType:
        type === 'logo' ? 'LogoWidgetControls' : 'PatternWidgetControls',
    })
  }

  return {
    widget: activeWidget,
    updateWidget,
  }
}
